import { toQueryString } from '../utils/query-string.util';

export const SERVICE_PREFIX = {
  PRODUCT_SVC: '/product-svc/api',
  PRODUCT_SVC_EXT: '/product-svc-ext/api',
  PRODUCT_V1: '/product-v1/api',
  PUBLIC_V2: '/publicv2',
};

export const SOCIAL_MEDIA_ENDPOINTS = {
  // Your existing endpoints
  SOCIAL_MEDIA_POST: {
    getPostsList: (page = 0, size = 50) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/socialMediaPosts/pagination/list?page=${page}&size=${size}`,
    getPostById: (postId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPost/${postId}`,
    addPost: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPost`,
    editPost: (postId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPost/${postId}`,
    deletePost: (postId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPost/${postId}`,
    approvePost: (postId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/aws/socialMediaPost/${postId}/approve`,
    uploadPostImage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/posts/image`, // Add this
    scheduleMultiplePosts: `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPosts/schedule/all`,
    // Uploads one image straight to the public asset bucket and returns its S3 url,
    // so the post request itself only carries urls and stays well under the payload cap.
    uploadPostAsset: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/editor/assets?type=social-media-post`,
    getPresignedUrl: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/editor/assets/presigned-url`,
  },

  // New Social Media Library endpoints
  SOCIAL_MEDIA_FILE_MANAGER: {
    getFolderListWithPagination: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/library/folder`,
    getFolderById: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialmedia/folder/${folderId}`,
    getFileById: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/libraries/${fileId}`,
    getAllLibraries: (page = 0, size = 10, search = '', tags = null) => {
      if (tags) {
        return `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/libraries?page=${page}&size=${size}&search=${search}&tags=${tags}`;
      } else {
        return `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/libraries?page=${page}&size=${size}&search=${search}`;
      }
    },
    getSubfoldersAndFiles: (
      folderId: number,
      tags = '',
      page = 0,
      size = 1000,
      search = ''
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/libraries/folder/${folderId}?tags=${tags}&page=${page}&size=${size}&search=${search}`,
    addFolder: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialmedia/folder`,
    editFolder: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialmedia/folder/${folderId}`,
    deleteFolder: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialmedia/folder/${folderId}`,
    addFilesToFolder: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/library`,
    editFile: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/library/${fileId}`,
    deleteFile: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/library/${fileId}`,
    bulkDownload: `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/download-zip`,
  },

  SOCIAL_MEDIA_TAGS: {
    getTags: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/tag/list`,
    addTag: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/tag`,
    deleteTag: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/tag/${id}`,
    updateTag: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMedia/tag/${id}`,
  },
};

export const API_ENDPOINTS = {
  CHATBOT_ENDPOINTS: {
    chatConfigs: `${SERVICE_PREFIX.PRODUCT_SVC}/chatconfigs`,
    chatConfigsUpload: `${SERVICE_PREFIX.PRODUCT_SVC}/chatconfigs/upload`,
    chatConfigsImage: `${SERVICE_PREFIX.PRODUCT_SVC}/chatConfigs/image`,
    v1chatbotQuestionnaire: `${SERVICE_PREFIX.PRODUCT_V1}/v1/chatbot/questionnaire`,
    v1formbuilderupdateQuestionnaire: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${id}/questions`,
    v1chatbotQuestionnaireQuestions: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/chatbot/questionnaire/${id}/questions`,
    addquestionnaireQuestions: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${id}/questions`,
    updatequestionnaireQuestionsorder: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${id}/questions/order`,
    deletequestionnaireQuestions: (
      questionnaireId: number,
      questionId: number
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${questionnaireId}/questions/${questionId}`,
    questionnaire: `${SERVICE_PREFIX.PRODUCT_SVC}/chatquestionnaire`,
    deleteQuestionnaire: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/chatquestionnaire/${id}`,
    chatSessionCount: `${SERVICE_PREFIX.PRODUCT_SVC}/chatsessions/count`,
    chatSessions: `${SERVICE_PREFIX.PRODUCT_SVC}/chatsessions/paged`,
    chatSessionMessages: (chatSessionId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/chatsessions/${chatSessionId}/messages`,
  },

  PUBLIC_V2_ENDPOINTS: {
    getPasswordResetConfirmationCode: (userName: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/users/forgot-password?username=${userName}`,
    forgotPassword: `${SERVICE_PREFIX.PUBLIC_V2}/users/forgot-password`,
    sendResetPasswordLinkByEmail: (email: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/users/reset-password?username=${email}`,
    businessOptimize: (businessId: number) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/businesses/${businessId}/optimize`,
    questionnaireColors: `${SERVICE_PREFIX.PUBLIC_V2}/questionnaire/colors`,
    questionnaireList: `${SERVICE_PREFIX.PUBLIC_V2}/questionnaire/list`,
    questionnaireById: (questionnaireId: number) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/questionnaire/${questionnaireId}`,
    manualQuestionnaire: (questionnaireId: number) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/manual/questionnaire/${questionnaireId}`,
    clinicsAll: `${SERVICE_PREFIX.PUBLIC_V2}/clinics/all`,
    serviceCategoriesAll: `${SERVICE_PREFIX.PUBLIC_V2}/serviceCategories/all`,
    emailSmsPackage: `${SERVICE_PREFIX.PUBLIC_V2}/emailSmsPackage`,
    analyticsTrack: `${SERVICE_PREFIX.PUBLIC_V2}/analytics/track`,
    getServicesForSymptom: (
      bodyPart: string,
      gender: string,
      symptom: string
    ) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/dental/consultationService?bodyPart=${bodyPart}&gender=${gender}&symptom=${encodeURIComponent(symptom)}`,
    optOutEmail: (userType: string, email: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/optout-email/${encodeURIComponent(userType)}?email=${encodeURIComponent(email)}`,
  },

  // Self-service API keys (business user). Gateway: /product-svc-ext/api/v1/business/api-tokens;
  // local-api.config rewrites the /product-svc-ext/api prefix → /api for localhost.
  BUSINESS_API_TOKEN_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/v1/business/api-tokens`,
    byId: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/v1/business/api-tokens/${id}`,
  },

  // Public GCLID contacts API (external clients + docs playground). Gateway: /publicv2/contacts/gclid;
  // local-api.config rewrites /publicv2 → /api/public/v2 for localhost.
  PUBLIC_GCLID_ENDPOINTS: {
    contactsGclid: `${SERVICE_PREFIX.PUBLIC_V2}/contacts/gclid`,
  },

  SUBSCRIPTION_ENDPOINTS: {
    emailSmsPackage: `${SERVICE_PREFIX.PRODUCT_SVC}/emailSmsPackage`,
    subscribeSubscription: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/subscribe/${id}`,
    unSubscribeSubscription: (subscriptionId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/unsubscribe/${subscriptionId}`,
    upgradeSubscription: `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/payments/upgrade`,
    getSubscriptionPayments: `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/payments`,
    getCustomerPaymentDetails: `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/get_customer_payment_details`,
    getSubscriptions: `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/getSubscriptions`,
    updateDefaultPayment: (id: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/subscription/update_default_payment/${id}`,
  },

  TWILIO_ENDPOINTS: {
    saveTwilioTemplates: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/twilio/privacy_terms_page_request`,
    getTwilioAvailableNumbers: (number: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/twilio/available_numbers/${number}`,
    getTwilioResponse: () => `${SERVICE_PREFIX.PRODUCT_SVC}/twilio/get_a2p`,
    saveTwilioSetupData: () => `${SERVICE_PREFIX.PRODUCT_SVC}/twilio/save_a2p`,
    activateTwoWayText: (businessId: number) =>
      `/backoffice/businesses/${businessId}/twilio-two-way-text`,
    deactivateTwoWayText: (businessId: number) =>
      `/backoffice/businesses/${businessId}/twilio-two-way-text`,
    getTwoWayTextHistory: (
      businessId: number,
      page: number,
      pageSize: number
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/businesses/${businessId}/two-way-text/history?page=${page}&pageSize=${pageSize}`,
  },

  AUTH_ENDPOINTS: {
    getUserByEmail: '/auth/v1/users/byemail',
    newAuthLogin: '/auth/login',
    logout: '/auth/logout',
    oldAuth: '/auth',
    newTotpSetup: '/auth/mfa/totp/setup',
    newTotpVerify: '/auth/mfa/totp/verify',
    newTotpVerifySetup: '/auth/mfa/totp/verify-setup',
    newSmsSetup: '/auth/mfa/sms/setup',
    newSmsVerifySetup: '/auth/mfa/sms/verify-setup',

    newSmsVerify: '/auth/mfa/sms/verify',

    loginByEmailAndPass: '/auth',
    loginWithCredsAndBid: `${SERVICE_PREFIX.PRODUCT_SVC}/user/auth`,
    changePassword: `${SERVICE_PREFIX.PRODUCT_SVC}/users/change-password`,

    generateAuthKey: `${SERVICE_PREFIX.PRODUCT_SVC}/keys`,
  },

  EMAIL_SMS_QUOTA: {
    quotaInfo: `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/email-sms/quota-details/subscription`,
    quotaV2: `${SERVICE_PREFIX.PRODUCT_V1}/v2/business/email-sms/quota`,
    quotaPurchases: `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/quota-purchases`,
  },

  BUSINESS_ENDPOINTS: {
    business: '/businesses',
    businesses: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/businesses/${id}`,
    giaRequest: (businessId: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/businesses/${businessId}/giaRequest`,
    v1businessProfile: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}`,
    businessUploadLogo: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/businesses/${id}/logo`,
    businessNotification: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/businesses/${id}/notification`,
    notificationPreferences: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/business-notification/preference`,
    bulkNotificationPreferenceUpdate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/business-notification/preference/bulk-update`,
    updatePersonalization: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${id}/personalization`,
    v1emailSmsBusiness: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}`,
    v1emailSmsCount: `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/email-sms/count`,
    v1triggerAudit: (
      params: Record<string, string | number | boolean | null | undefined>
    ) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/email-sms/trigger/count?${toQueryString(
        params
      )}`,
    v1emailSmsLead: (communicationType: string, triggerId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/email-sms/trigger/${triggerId}?communicationType=${communicationType}&triggerModule=leads`,
    v1leadContent: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/lead/content?id=${id}`,
    optedOutEmailList: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/optout/list/${id}`,
    optedOutSMSList: () => `${SERVICE_PREFIX.PRODUCT_SVC}/optout/sms/list`,
    getBusinessConfig: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-config/${id}`,
    updateBusinessConfig: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-config/${id}`,
  },

  BUSINESS_DOMAIN_ENDPOINTS: {
    getDomains: (businessId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-domains/${businessId}`,

    addDomain: (businessId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-domains/${businessId}`,

    updateDomain: (domainId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-domains/${domainId}`,

    deleteDomain: (domainId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/business-domains/${domainId}`,
  },

  STRIPE_ENDPOINTS: {
    onboard: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/stripe/onboard`,
    resumeSetup: (accountId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/stripe/resume-onboard?accountId=${accountId}`,
    account: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/stripe/account`,
    deleteAccount: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/stripe/account/${id}`,
    getStripeAccount: `${SERVICE_PREFIX.PRODUCT_SVC}/package/payment/stripe/account`,
    postStripeIntent: `${SERVICE_PREFIX.PRODUCT_SVC}/package/payment/stripe/intent`,
    postStripeConfirm: `${SERVICE_PREFIX.PRODUCT_SVC}/package/payment/stripe/confirm`,
    fetchStripeAccountDetails: `${SERVICE_PREFIX.PRODUCT_SVC}/payment/stripe/account`,
    createPaymentIntent: `${SERVICE_PREFIX.PRODUCT_SVC}/payment/stripe/intent`,
    confirmPayment: `${SERVICE_PREFIX.PRODUCT_SVC}/payment/stripe/confirm`,
  },

  API_LOG_ENDPOINTS: {
    getHistoryLogCount: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/histories/count?type=BUSINESS_CREATION`,
    getApiIntegrationData: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations?type=BUSINESS_CREATION`,
    getApiIntegrationHistory: (page: number, size: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/histories?type=BUSINESS_CREATION&page=${page}&size=${size}`,
    getQuestionnaireV1: `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire`,
    addApiIntegration: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations`,
    toggleApiIntegration: (id: number, disable: boolean) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/enable?id=${id}&disable=${disable}`,
    getApiIntegrationthirdParty: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/thirdParty?id=${id}&type=BUSINESS_CREATION`,
  },

  USER_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC}/users`,
    v1User: `${SERVICE_PREFIX.PRODUCT_V1}/v1/user/`,
    deleteUser: (userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/users/${userId}`,
    deleteProfileImage: (userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/users/${userId}/profile/delete`,
    v1UserList: `${SERVICE_PREFIX.PRODUCT_V1}/v1/users/all`,
    v1InboxUserList: `${SERVICE_PREFIX.PRODUCT_V1}/v1/inbox/users/all`,
    providerListByServiceId: (ids: number[]) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/providers/dropDown?serviceId=${ids.join(',')}`,
    changePassword: `${SERVICE_PREFIX.PRODUCT_SVC}/users/change-password`,
    notificationPreferences: (userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/notification-preferences/${userId}`,
    bulkNotificatonPreferenceUpdate: `${SERVICE_PREFIX.PRODUCT_SVC}/notification-preferences/bulk-update`,
    verifyEmail: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/users/verify-email?token=${encodeURIComponent(token)}`,
  },
  TIMEZONE_ENDPOINTS: {
    v1Timezones: `${SERVICE_PREFIX.PRODUCT_V1}/timezones`,
  },

  // const apiUrl = '/api/clinics/update1/' + clinicId + '/' + isDefault;

  CLINIC_ENDPOINTS: {
    v1AllClinics: `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics/allClinics`,
    v1AllClinicsDropdown: (
      view: 'list' | 'dropdown' | 'summary' = 'dropdown'
    ) => `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics?view=${view}`,
    v1ClinicServiceCategories: `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics/serviceCategories`,
    v1CategoriesServices: `${SERVICE_PREFIX.PRODUCT_V1}/v1/services/serviceCategories`,
    clinic: `${SERVICE_PREFIX.PRODUCT_SVC}/clinics`,
    detailedClinicList: `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics`,
    v2AllClinics: `${SERVICE_PREFIX.PRODUCT_V1}/v2/clinic/list`,
    createClinicV1: `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics`,
    patchClinicV1: (clinicId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics/${clinicId}`,
    getClinicDetailsV1: (clinicId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/clinics/${clinicId}`,
    clinicReviewDetail: (id: string | number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/clinic/${id}/clinicreview`,
    businessReviewConfiguration: (businessId: string | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/review/config/${businessId}`,
    saveBusinessReviewConfiguration: (clinicId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/clinics/${clinicId}/reviews/config`,
    refreshReview: (clinicId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/clinic/${clinicId}/scrap`,
    getServicesByClinicId: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/services/dropDown?clinicId=${id}`,
  },

  SERVICE_ENDPOINTS: {
    deleteServiceById: (serviceId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/services/${serviceId}`,
    v1ServicesList: `${SERVICE_PREFIX.PRODUCT_V1}/v1/services/all`,
    servicePostion: `${SERVICE_PREFIX.PRODUCT_SVC}/services/position`,
    base: `${SERVICE_PREFIX.PRODUCT_SVC}/services`,
    exportServices: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/services/export`,
    importServices: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/services/import`,
    getImportHistory: (page = 0, size = 50) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/services/import-history?page=${page}&size=${size}`,
  },

  CATEGORY_ENDPOINTS: {
    createCategory: `${SERVICE_PREFIX.PRODUCT_SVC}/servicecategories`,
    categoryById: (categoryId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/servicecategories/${categoryId}`,
    servicePostion: `${SERVICE_PREFIX.PRODUCT_SVC}/servicecategories/position`,
    categoryByClinicId: `${SERVICE_PREFIX.PRODUCT_SVC}/clinics/servicecategories?clinicId=`,
    getCategoryListV1: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/v1/serviceCategories/all`,
  },

  CONSENT_ENDPOINTS: {
    v1AllConsents: `${SERVICE_PREFIX.PRODUCT_V1}/v1/consents`,
    assignConsents: (consentIds: number[], leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${leadId}/consents?consentIds=${consentIds.join(',')}`,
  },

  QUESTIONNAIRE_ENDPOINTS: {
    defaultQuestionnaire: `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/lead-capture-form-id`,
    v1AllQuestionnaire: `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire`,
    assignQuestionnaires: (questionnaireIds: number[], leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${leadId}/questionnaire?questionnaireIds=${questionnaireIds.join(',')}`,
  },

  FORM_BUILDER: {
    getQuestionnaireV1: `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire`,
    createForm: `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire`,
    getFormById: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${formId}`,
    deleteForm: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}`,
    updateForm: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}`,
    patchForm: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${formId}`,
    getFormNotificationDetail: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/notifications`,
    getAvailableQuestionByFormId: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/questions`,
    addBackgroundImages: (formId: number, index: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/backgroundImage/${index}`,
    getFormSubmissions: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/questionnaire-submissions`,
    getFormSubmissionsDetails: (formSubmissionId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/questionnaire-submissions/${formSubmissionId}`,
    addUpdateFormConditions: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${formId}/conditions`,
    updateFormDetails: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${formId}`,
    removeBackgroundImage: (formId: number, index: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/backgroundImage/${index}`,
    getFormHistoryByFormId: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/form-audit/${formId}`,
    updateNotifications: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/${formId}/notifications`,
    addDefaultBackgroundImage: (formId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/${formId}/backgroundImage/all`,
  },

  CAMPAIGN: {},

  WORKING_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/provider`,
    v1Working: `${SERVICE_PREFIX.PRODUCT_V1}/v1/user`,
  },

  VACATION_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/provider`,
    v1Vacation: `${SERVICE_PREFIX.PRODUCT_V1}/v1/user`,
  },

  REVIEWS: {
    reviews: '/api/reviews',
    postCustomReviewConfig: `${SERVICE_PREFIX.PRODUCT_SVC}/customize/review`,
    getCustomReviewConfig: `${SERVICE_PREFIX.PRODUCT_SVC}/review/get/review/configdata/`,
    postSetupReviewDestination: `${SERVICE_PREFIX.PRODUCT_SVC}/review/setup/destination`,
    getSetupReviewDestination: `${SERVICE_PREFIX.PRODUCT_SVC}/review/get/review/destination`,
    getClinicReviewInfo: (clinicId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/clinic/${clinicId}/clinicreview`,
    getClinicReviewInfoSecond: '/api/clinicreview',
    getClinicReviewNetPromoterStatistics: (clinicId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/reviewRatings/clinic/${clinicId}/stats`,
    postReviewDraft: `${SERVICE_PREFIX.PRODUCT_SVC}/review/save/reviewdraft`,
    getReviewDraft: `${SERVICE_PREFIX.PRODUCT_SVC}/review/get/reviewdraft`,
    getReviewDraftById: (clinicId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/review/get/reviewdraft/${clinicId}`,
    getReviewSettingsHistory: (clinicId: number | string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/review/history/${clinicId}`,
    getFeedbackData: (businessId: number, pageNum: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/reviewRatings/feedback/${businessId}/${pageNum}`,
    setupGoogleProfile: `${SERVICE_PREFIX.PRODUCT_SVC}/social-profiles/google`,
    googleSetupStatus: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/google/account/status`,

    //Review Manager Endpoints
    getClinicReviewStatistics: (clinicId: number | string, filter: string) =>
      `/rw/clinic/clinic-analytics/${clinicId}/${filter}`,
    getReviewDataWithoutFilter: (pageNum: number) =>
      `/rw/fetch/review/${pageNum}`,
    getReviewData: (businessId: number, pageNum: number) =>
      `/rw/fetch-review-by-filter/${businessId}/${pageNum}`,
    updateReview: '/rw/hide/reviews',
    createReview: (clinicId: number) => `/rw/${clinicId}/reviews/add`,
    updateReviews: (clinicId: number) => `/rw/edit/reviews/${clinicId}`,
    deleteReview: (clinicId: number) => `/rw/reviews/${clinicId}`,
    getReviews: (clinicId: number, pageNum: number, searchText: string) =>
      `/rw/search/reviews/${clinicId}/${pageNum}?searchText=${searchText}`,
    hideOrUnhideReview: (clinicId: number, hideValue: boolean) =>
      `/rw/hide/reviews/${clinicId}/${hideValue}`,
  },

  LANDING_PAGE_ENDPOINTS: {
    getLandingPageList: (
      type: 'my' | 'services' | 'others',
      page: number,
      size: number,
      search: string
    ) => {
      switch (type) {
        case 'services':
          return `${SERVICE_PREFIX.PRODUCT_V1}/v1/landing-pages/services?search=${search}&tags=`;
        case 'others':
          return `${SERVICE_PREFIX.PRODUCT_V1}/v1/landing-pages/others?search=${search}&tags=`;
        case 'my':
        default:
          return `${SERVICE_PREFIX.PRODUCT_V1}/v1/landingPage/page?page=${page}&size=${size}&search=${search}`;
      }
    },
    getLandingPage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/landingpage/edit`,
    createLandingPage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/landingpage`,
    editLandingPage: (pageId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/landingpage/${pageId}`,
    deleteLandingPage: (pageId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/landingpage/${pageId}`,
    useTemplate: (pageId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/adminlandingpage/${pageId}`,
  },

  AI_ENDPOINTS: {
    aiGptStream: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/chatgpt/stream`,
  },

  // Suggests Self-Assessment body areas and symptoms while a service is being created.
  // Bare path with no PRODUCT_SVC prefix: this is served by g99-ai-service, reached through
  // ServiceType.AI_SERVICE, the same way AI_SEO_AUDITOR is.
  SERVICE_SYMPTOM_ENDPOINTS: {
    suggest: '/api/v1/service-symptoms/suggest',
    // Whole mapping in one transactional call. Same prefix as the rest of SERVICE_ENDPOINTS, so
    // it goes through the gateway route services already use. The per-row endpoints under
    // SELF_ASSESSMENT_ENDPOINTS are unchanged and still used by the Self-Assessment screen.
    saveMappings: (serviceId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/services/${serviceId}/symptom-mappings`,
  },

  AI_SEO_AUDITOR: {
    runAudit: '/api/v1/seo-auditor/audit',
  },

  AI_SOCIAL_ENDPOINTS: {
    suggestFestivalPosts: '/api/social-media/suggest/post/festival',
    suggestGenericPosts: '/api/social-media/suggest/posts',
    generateImage: '/api/social-media/generate/image',
    savePostToDb: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/posts`,
    getDraftPosts: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/posts`,
  },

  AI_SALES_ENDPOINTS: {
    analyzeCompetitors: '/api/sales/analyze-competitors',
    analyzeStatus: '/api/sales/analysis-status',
    fetchCompetitors: '/api/sales/competitors',
  },
  AI_LANDING_PAGE_ENDPOINTS: {
    GENERATE: '/api/landing-page/generate',
    STITCH_GENERATE: '/api/landing-page/stitch/generate',
    STITCH_REFINE: '/api/landing-page/stitch/refine',
    STITCH_CREDENTIALS: '/api/landing-page/stitch/credentials',
  },
  TED: {
    config: `${SERVICE_PREFIX.PRODUCT_SVC}/ted/config`,
    ask: `${SERVICE_PREFIX.PRODUCT_SVC}/ted/ask`,
  },
  AI_LUCY: {
    /** Social Media Post Context Sync — scheduler status and recent run history. */
    socialSyncRuns: (businessId: string | number, limit = 5) =>
      `/api/v1/social-content/${businessId}/runs?limit=${limit}`,
    /** Triggers a sync immediately; the response describes what the run actually did. */
    socialSyncRunNow: (businessId: string | number) =>
      `/api/v1/social-content/${businessId}/sync`,
    activateLucy: (businessId: string | number) =>
      `/api/v1/ai-responder/activate?businessId=${businessId}`,
    activationProgress: (businessId: string | number) =>
      `/api/v1/ai-responder/activation-progress?businessId=${businessId}`,
    createConfig: '/api/v1/ai-responder/config',
    updateServicePriceDisclosure: (serviceId: number | string) =>
      `/api/v1/ai-responder/service/${serviceId}`,
    submitAIFeedback: () => '/api/v1/ai-responder/feedback',
    // Reported after a manual reply is sent over a waiting GIA suggestion. The service decides
    // whether that really was an ignored suggestion and records the thumbs-down itself.
    ignoredSuggestionFeedback:
      '/api/v1/ai-responder/feedback/ignored-suggestion',
    undoSaveForLater: '/api/v1/ai-responder/feedback/undo',
    getFeedback(outgoingMessageId: string | number) {
      return `/api/v1/ai-responder/feedback/${outgoingMessageId}`;
    },
    getAuditData(
      messageId: string | number | null | undefined,
      channelType: string,
      auditId = 0
    ) {
      const msgId = messageId ?? 0;
      const auditQuery = auditId ? `?auditId=${auditId}` : '';
      return `/api/v1/ai-responder/audits/${msgId}/${channelType}${auditQuery}`;
    },
    getSources(messageId: string | number, channelType: string) {
      return `/api/v1/ai-responder/audits/${messageId}/${channelType}/references`;
    },
    getVoicemailRecordingUrl(callSid: string) {
      return `/api/v1/ai-responder/voicemail/recording-url?callSid=${callSid}`;
    },
    downloadKnowledgeBaseFile: (id: number) =>
      `/api/v1/ai-responder/knowledge-base/download/${id}`,
    knowledgeGraph: '/api/v1/knowledge-base-config/graph',
    enableGia: (businessId: number | string) =>
      `/api/v1/ai-responder/enable?businessId=${businessId}`,
    // Business comes from the X-TenantID header, never the URL, so a client can only ever
    // read or write their own instructions.
    giaInstructions: '/api/v1/gia/instructions',
    giaInstructionsHistory: (page: number, size: number) =>
      `/api/v1/gia/instructions/history?page=${page}&size=${size}`,
    disableGia: (businessId: number | string) =>
      `/api/v1/ai-responder/disable?businessId=${businessId}`,
    VOICE_AGENT: {
      config: '/api/voice-agent/business/config',
      purchasePlan: (planId: number | string, businessId: number | string) =>
        `/api/voice-agent/business/plans?planId=${planId}&businessId=${businessId}`,
      plans: '/api/voice-agent/plans',
      setActive: '/api/voice-agent/business/active',
    },
    GOOGLE_CALENDAR: {
      handleCallback: '/api/v1/ai-responder/google/calendars',
      disconnect: '/api/v1/ai-responder/google/calendars/disconnect',
      events: '/api/v1/ai-responder/google/calendars/events',
    },
    FAQ_API_ENDPOINTS: {
      getFaqs: '/api/v1/local-faq',
      deleteFaq: (id: string) => `/api/v1/local-faq/${id}`,
      bulkSave: '/api/v1/local-faq/bulk-save',
      bulkUpdate: '/api/v1/local-faq/bulk-update',
      getLocalFaqs: '/api/v1/local-faq',
      getFeedbackFaqs: '/api/v1/ai-responder/feedback',
    },
    ANALYTICS: {
      knowledgeSummary: '/api/v1/ai-responder/analytics/knowledge-summary',
      giaActivity: (timeframe = 'week') =>
        `/api/v1/ai-responder/analytics/gia-activity?timeframe=${timeframe}`,
      // startDate/endDate (yyyy-MM-dd) win over `filter` server-side: the endpoint returns a
      // custom range whenever both are present, which is how we get a true trailing window
      // instead of the calendar buckets `filter` supports.
      giaUsage: (filter = 'week', startDate?: string, endDate?: string) => {
        const base = `/api/v1/ai-responder/analytics/usage?filter=${filter}`;
        return startDate && endDate
          ? `${base}&startDate=${startDate}&endDate=${endDate}`
          : base;
      },
      giaActivityDaily: (days = 7) =>
        `/api/v1/ai-responder/analytics/gia-activity/daily?days=${days}`,
      aggregateToday:
        '/api/v1/ai-responder/analytics/gia-activity/aggregate-today',
      feedback: (channel: string) =>
        `/api/v1/ai-responder/analytics/feedback?channel=${channel}`,
      urlTracking: '/api/url-tracking',
      urlTrackingMetrics: (id: number) => `/api/url-tracking/tracking?id=${id}`,
      bookingUrlHistory: '/api/url-tracking/booking-url-history',
    },
    chatQuery: '/api/v1/ai-responder/query',
    resetGiaConfigurations: (businessId: string | number) =>
      `/api/v1/ai-responder/reset?businessId=${businessId}`,

    ANSWERING_SCORE: {
      getOverallScore: '/api/v1/ai-responder/business/answering-score',
      getTierQuestions: (page = 0, size = 10, priority: string) =>
        `/api/v1/ai-responder/business/tier/question-answer?page=${page}&size=${size}&priority=${encodeURIComponent(
          priority
        )}`,
      runBaseline: '/api/v1/ai-responder/business/run',
      getBaselineHistory: (page = 0, size = 20) =>
        `/api/v1/ai-responder/business/baseline-history?page=${page}&size=${size}`,
    },
    getBaselineGraph: (period: string, groupBy?: string) => {
      let url = `/api/v1/ai-responder/business/baseline-graph?period=${encodeURIComponent(
        period
      )}`;
      if (groupBy) {
        url += `&groupBy=${encodeURIComponent(groupBy)}`;
      }
      return url;
    },
    deleteKnowldgeBasefile: (
      configId: string | number,
      fileId: string | number
    ) =>
      `/api/v1/ai-responder/config/${configId}/knowledgebase/files/${fileId}`,
    contactsAiEnabled: '/api/v1/ai-responder/contacts/ai-enabled',
    DOCUMENT_INTELLIGENCE_API_ENDPOINTS: {
      updateAIIntelligenceFile: '/api/v1/ai-document-intelligence',
      deleteAIIntelligenceFile: '/api/v1/ai-document-intelligence',
      clearDocumentChatHistory:
        '/api/v1/ai-document-intelligence/clear-chat-history',
      getAIIntelligenceFile: '/api/v1/ai-document-intelligence',
      getAIIntelligenceFileHistory:
        '/api/v1/ai-document-intelligence/get-document-history',
      getDocumentSummary: '/api/v1/ai-document-intelligence/generate-summary',
      chatWithDocument: '/api/v1/ai-document-intelligence/chat',
    },
    APPOINTMENT: {
      BOOKING_CONFIG: '/api/v1/ai-responder/config/booking-config',
      HISTORY: '/api/v1/ai-responder/appointment-history',
      REQUESTED_PLATFORMS: '/api/v1/ai-responder/requested-platforms',
    },
  },

  AI_AGENTS_ENDPOINTS: {
    getAllPrompts: '/api/v1/prompts/all',
    getPromptById: (promptId: string | number) => `/api/v1/prompts/${promptId}`,
    dryRun: '/api/v1/prompts/dry-run',
    getAllBlogPrompts: '/api/v1/prompts?sort=id,desc&page=0&size=10&types=BLOG',
    generateBlog: '/api/blogs/generate',
    suggestBlogWeb: '/api/blogs/suggest/web',
    suggestBlogWebFestival: '/api/blogs/suggest/web-festival',
    getAllSnapshots: (businessId: string | number) =>
      `/api/v1/snapshots/details?businessId=${businessId}`,
    getSummary: (businessId: string | number, startDate: string) =>
      `/api/v1/businesses/${businessId}/summary?startDate=${startDate}`,
    getBusinessTypeCounts: (businessId: string | number, fromDate: string) =>
      `/api/v1/businesses/${businessId}/business-type-counts?fromDate=${fromDate}`,
  },

  ROLE_ENDPOINTS: {
    v1roles: `${SERVICE_PREFIX.PRODUCT_V1}/v1/roles`,
  },

  CALL_RAIL: {
    callRailView: (view: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/call-rail?view=${view}`,
  },

  TRIGGER_ENDPOINTS: {
    trigger: `${SERVICE_PREFIX.PRODUCT_SVC}/trigger`,
    triggerById: (id: number): string =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/${id}`,
    tagTrigger: `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/tag`,
    v1TriggerQuestionnaire: `${SERVICE_PREFIX.PRODUCT_V1}/v1/trigger/questionnaire`,
    v1LandingPage: `${SERVICE_PREFIX.PRODUCT_V1}/v1/landingPage/pageNames`,
    v1TriggerEdit: `${SERVICE_PREFIX.PRODUCT_V1}/v1/trigger/edit`,
    patientTagList: `${SERVICE_PREFIX.PRODUCT_SVC}/tag/list`,
    leadTagList: `${SERVICE_PREFIX.PRODUCT_SVC}/tag/list`,
    leadSourceList: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/lead-sources/list`,
    addLeadSourceUrl: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/leadsourceurls`,
    leadSourceUrlsList: (
      sources?: string[],
      landingPages?: string[],
      forms?: string[]
    ): string => {
      const url = `${SERVICE_PREFIX.PRODUCT_SVC}/lead-sources/urls?`;
      const params: string[] = [];

      if (sources && sources.length > 0) {
        params.push(`source=${sources.join(',')}`);
      }

      if (landingPages && landingPages.length > 0) {
        params.push(`landingPage=${landingPages.join(',')}`);
      }

      if (forms && forms.length > 0) {
        params.push(`forms=${forms.join(',')}`);
      }

      return url + params.join('&');
    },
    v1TriggerReview: `${SERVICE_PREFIX.PRODUCT_V1}/v1/trigger/questionnaire?type=REVIEW`,
    getTriggerForEdit: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/${id}`,
    getTagTriggerForEdit: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/tag/${id}`,
    getTriggerHistory: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger-audit/${id}`,
    getTriggerData: (
      page: number,
      size: number,
      moduleName: string,
      status?: string
    ): string => {
      let url = `${SERVICE_PREFIX.PRODUCT_SVC}/triggers?page=${page}&size=${size}&moduleName=${moduleName}`;
      if (status && status !== 'All') {
        url += `&status=${status}`;
      }
      return url;
    },

    deleteTrigger: (id: number): string =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/${id}`,
    updateTriggerStatus: (id: number): string =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/status/${id}`,
    getTriggerById: (id: number): string =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/${id}`,
    migrateExistingTriggers: `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/migrate`,
    getQuestions: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/leadSources/question`,
  },

  TASKS_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC}/workflowtasks`,
    getAllTasks: `${SERVICE_PREFIX.PRODUCT_SVC}/workflowtasks/all`,
    deleteAndUpdateTaskById: (taskId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/workflowtasks/${taskId}`,
    v1UpdateTask: `${SERVICE_PREFIX.PRODUCT_V1}/v1/workflowtasks`,
    v1GetTaskList: `${SERVICE_PREFIX.PRODUCT_SVC}/workflowtasks`,
    getTasksByLead: (leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/workflowtasks/lead/${leadId}`,
    getLeadDetails: (leadId: number) => `/questionnaire-submissions/${leadId}`,
  },

  CAMPAIGNS_ENDPOINTS: {
    aiBlastPreviewMessage: '/api/v1/ai-blast/preview',
    campaignDashboard: (moduleName: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai-campaign/dashboard?moduleName=${encodeURIComponent(moduleName)}`,
    campaignList: (
      moduleName: string,
      page: number,
      pageSize: number,
      search?: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai-campaign?moduleName=${encodeURIComponent(moduleName)}&page=${page}&pageSize=${pageSize}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    deleteCampaign: `${SERVICE_PREFIX.PRODUCT_SVC}/trigger/`,
    v1Campaign: `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns`,
    matchingContacts: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts`,
    getCampaignById: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns/${id}`,
    getCampaignDetailsForEdit: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns/${id}/details`,
    cancelCampaignById: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns/${id}/cancel`,
    updateCampaignById: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns/${id}`,
    getCampaignAuditDetails: (
      id: number,
      communicationType: string,
      triggerModule: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/new/mass-email-sms/trigger/${id}?communicationType=${communicationType}&triggerModule=${triggerModule}`,
    retryCampaignSms: (auditId: string | number, moduleName: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/notifications/retry-failed-message?auditId=${encodeURIComponent(
        String(auditId)
      )}&moduleName=${encodeURIComponent(moduleName)}`,
    getCampaignAuditStatusSummary: (id: number, communicationType: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/campaigns/audit-status-summary/${id}?communicationType=${communicationType}`,
    exportAudit: (
      id: number,
      communicationType: string,
      triggerModule: string,
      format: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/audit/export/mass-email-sms/trigger/${id}?communicationType=${communicationType}&triggerModule=${triggerModule}&format=${format.toUpperCase()}`,
    getCampaignContactAudit: (id: number, page: number, pageSize: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/contact-audit/${id}?page=${page}&pageSize=${pageSize}`,
    getCampaignEngagement: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/engagement/${id}`,
    getTriggerEnrollments: (
      id: number,
      page: number,
      pageSize: number,
      status?: string,
      search?: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/enrollments/${id}?page=${page}&pageSize=${pageSize}${
        status ? `&status=${encodeURIComponent(status)}` : ''
      }${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getContactMessageSchedule: (triggerId: number, contactId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/contact-schedule/${triggerId}/${contactId}`,
    getCampaignAuditStatusSummaryV2: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/audit-status-summary/${id}`,
    getCampaignOverview: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/overview/${id}`,
    getCampaignGuardrails: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/ai-campaign/guardrails/${id}`,
    legacySequences: (
      moduleName: string,
      status: string,
      page: number,
      size: number
    ) => {
      let url = `${SERVICE_PREFIX.PRODUCT_SVC}/legacy-sequences?page=${page}&size=${size}`;
      if (moduleName && moduleName !== 'All') {
        url += `&moduleName=${encodeURIComponent(moduleName)}`;
      }
      if (status && status !== 'All') {
        url += `&status=${encodeURIComponent(status)}`;
      }
      return url;
    },
  },

  TEMPLATE_ENDPOINTS: {
    getTemplateVariable: (type: string, smsTemplateName: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${type}templatenames/${smsTemplateName}/variables`,
    getVariableDetails: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/smstemplates/variablesData`,
    createSMSTemplate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/smstemplates`,
    createEmailTemplate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailtemplates`,
    getEmailTemplateNames: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailtemplatenames`,
    testEmailTemplate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailtemplates/test/template`,
    testSmsTemplate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/smstemplates/test/template`,
    getContactSubmissions: `${SERVICE_PREFIX.PRODUCT_SVC}/test/questionnaire/contact-submissions`,
    updateEmailTemplateStatus: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/emailTemplates/${templateId}/status`,
    updateSmsTemplateStatus: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/smsTemplates/${templateId}/status`,
    updateEmailTemplate: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailtemplates/${templateId}`,
    updateSMSTemplate: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/smstemplates/${templateId}`,
    getSMSTemplateById: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/smstemplates/${templateId}`,
    getEmailTemplateById: (templateId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailtemplates/${templateId}`,
    cloneTemplate: (templateType: string, id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${templateType}/${id}/clone`,
    createTemplate: (templateType: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${templateType}`,
    updateTemplate: (templateType: string, id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${templateType}/${id}`,
    getTemplateById: (templateType: string, id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${templateType}/${id}`,
    deleteTemplate: (templateType: string, id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/${templateType}/${id}`,
  },

  CLIFFHANGER_ENDPOINTS: {
    v1addIntegration: `${SERVICE_PREFIX.PRODUCT_V1}/v1/integrations`,
    v1updateIntegration: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/integrations/${id}`,
    getIntegration: `${SERVICE_PREFIX.PRODUCT_V1}/v1/integrations`,
  },

  INBOX_ENDPOINTS: {
    auditMessage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages/send-manually`,
    saveDraftMessage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages/draft`,
    messages: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages`,
    messageDetail: (
      inboxId: number,
      contactId: number,
      opts?: { pageSize?: number; cursor?: string }
    ) => {
      const params = new URLSearchParams();

      if (inboxId) {
        params.set('inboxId', inboxId.toString());
      } else if (contactId) {
        params.set('contactId', contactId.toString());
      }

      if (opts?.pageSize) {
        params.set('pageSize', opts.pageSize.toString());
      }
      if (opts?.cursor) {
        params.set('cursor', opts.cursor);
      }

      return `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages/history?${params}`;
    },
    sendMentionEmails: `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission/notes/email`,
    leadDetails: (leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${leadId}`,
    contactAiStatus: (leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${leadId}/ai-status`,
    addNotesComment: (leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission-comment/${leadId}/comment/lead-notes-file`,
    addNotesCommentWithoutAttachment: `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission-comment`,
    deleteNotesComment: (commentId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission-comment/${commentId}`,
    getQuestionnaireList: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${id}/questionnaire`,
    getInboxConsents: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${id}/consents`,
    getInboxPayments: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/payment?contacts=${id}`,
    getSignUrl: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/consents/${id}/signurl`,
    updateConsentStatus: (appointmentConsentId: number, status: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/consents/${appointmentConsentId}/status?status=${status}`,
    downloadAppointmentConsent: (appointmentConsentId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/consents/${appointmentConsentId}/download`,

    v1getsmsAuditLogs: `${SERVICE_PREFIX.PRODUCT_V1}/v1/notifications/sms-audit-logs`,
    addOnlyNotesComment: `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission-comment`,
    //Note: This API is declared in Contact module So no need to add two times
    getLeads: (
      page: number,
      size: number,
      statusFilter: string,
      sourceFilter: string,
      search: string,
      leadTagFilter: string,
      startDate: string,
      endDate: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/contact-submissions/filter/pagination?page=${page}&size=${size}&statusFilter=${statusFilter}&sourceFilter=${sourceFilter}&search=${search}&leadTagFilter=${leadTagFilter}&startDate=${startDate}&endDate=${endDate}`,
    getLeadDetails: (leadId: number) =>
      `/api/questionnaire-submissions/${leadId}`,
    getsmsAuditLogsDetail: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/notifications/sms-audit-logs/source/${id}?sourceType=PATIENT`,
    toggleMarketingOptions: (contactId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${contactId}`,
    markMessageAsRead: (inboxId: number) => {
      // Create a URLSearchParams object for proper URL encoding
      const params = new URLSearchParams();
      if (inboxId) {
        params.append('inboxId', inboxId.toString());
      }
      return `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages/read?${params.toString()}`;
    },

    TAGS: {
      getTags: () => `${SERVICE_PREFIX.PRODUCT_SVC}/tag/list`,
      addTag: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/tag`,
      tagById: (tagId: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/tag/${tagId}`,
      assignTagToContact: (contactId: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission/lead/${contactId}/tags`,
      assignTagsToBulkContact: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/tags`,
    },
    sendCustomMessage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/sms`,
    sendCustomEmail: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/email`,
    sendInstagramMessage: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/message`,
    getTemplateForLead: (leadId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/emailSmsTemplates/Lead/${leadId}`,
    QUESTIONNAIRE: {
      getQuestionnaireList: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${id}/questionnaire`,
    },
    CONSENTS: {
      getInboxConsents: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${id}/consents`,
      updateConsentStatus: (appointmentConsentId: number, status: string) =>
        `${SERVICE_PREFIX.PRODUCT_SVC}/consents/${appointmentConsentId}/status?status=${status}`,
      getSignUrl: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC}/consents/${id}/signurl`,
      downloadAppointmentConsent: (appointmentConsentId: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC}/consents/${appointmentConsentId}/download`,
    },
    PAYMENT: {
      getInboxPayments: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${id}/payment`,
    },
    TIMELINE: {
      getContactTimeline: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_V1}/v1/contacts/${id}/timeline`,
    },
    updateChatStatusWithoutLeadId: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/messages/chat-status`,
  },

  CONTACTS_ENDPOINTS: {
    getAllContacts: (
      page: number,
      size: number,
      statusFilter: string,
      sourceFilter: string,
      search: string,
      leadTagFilter: string,
      startDate: string,
      endDate: string
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire/contact-submissions/filter/pagination?page=${page}&size=${size}&statusFilter=${statusFilter}&sourceFilter=${sourceFilter}&search=${search}&leadTagFilter=${leadTagFilter}&startDate=${startDate}&endDate=${endDate}`,
    updateQuestionnaire: (leadId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission/lead/${leadId}`,
    updateQuestionnaireAssignee: (leadId: number | null, userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission/lead/${leadId}/userId?userId=${userId}`,
    updateQuestionnaireAssigneeEmail: `${SERVICE_PREFIX.PRODUCT_SVC}/questionnaire-submission/user/userEmail`,
    fetchContact: (leadId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${leadId}`,
    deleteContact: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts`,
    fetchContactList: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts`,
    createContact: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts`,
    updateContact: (contactId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${contactId}`,
    massUpdateStatus: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/status`,
    contactStats: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/stats`,
    massUpdateState: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/stage`,
    bulkArchive: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/bulk/archive`,
    bulkUnarchive: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/bulk/unarchive`,
    archiveContact: (contactId: number, isUnarchive = false) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/${contactId}/${isUnarchive ? 'unarchive' : 'archive'}`,
    getColumnPreferences: (pageId: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/column-config/get?pageId=${pageId}`,
    saveColumnPreferences: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/column-config/save`,
    exportContacts: (
      format: string,
      params: {
        status: string;
        stage: string;
        source: string;
        search: string;
        utmSource: string;
        utmMedium: string;
        utmCampaign: string;
        utmTerm: string;
        utmContent: string;
      }
    ) =>
      format === 'pdf'
        ? `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/export/pdf?${toQueryString(params)}`
        : `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts/export/xls?${toQueryString(params)}`,
    getUtmParameters: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contact/utm/distinct/parameter`,
    getContactsDropdown: (
      page = 0,
      deleted = false,
      sortField = 'updatedAt',
      sortOrder = 'desc',
      view = 'dropdown'
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/contacts?page=${page}&deleted=${deleted}&sortField=${sortField}&sortOrder=${sortOrder}&view=${view}`,
  },

  REPORTS_ENDPOINTS: {
    getReportData: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}`,
    updatePaidMediaCode: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/businesses/${id}/paidMedia`,
    updateDataStudioCode: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/businesses/${id}/dataStudio`,
    syndication: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/syndicationReports`,
    websiteScorecard: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}?fields=websiteScorecard`,
    websiteAnalytics: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}?fields=dataStudioCode`,
    paidMediaAnalytics: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${id}?fields=paidMediaCode`,

    PLATFORM_REPORTS: {
      getContactInsights: (
        reportType: string,
        startDate: string,
        endDate: string,
        unit: string,
        // When true the server ignores the dates above and picks the narrowest window that has
        // leads, so the report never opens empty. Only sent on first load.
        autoFallback = false
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/insights?type=` +
        reportType +
        '&startDate=' +
        startDate +
        '&endDate=' +
        endDate +
        '&unit=' +
        unit +
        '&autoFallback=' +
        autoFallback,

      getContactDetailedInsights: (
        reportType: string,
        startDate: string,
        endDate: string,
        source: string
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/insights/source?type=` +
        reportType +
        '&startDate=' +
        startDate +
        '&endDate=' +
        endDate +
        '&source=' +
        source,

      getCallRailReport: (
        startDate: string,
        endDate: string,
        unit: string,
        autoFallback = false
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/call-rail/dashboard?startDate=` +
        startDate +
        '&endDate=' +
        endDate +
        '&unit=' +
        unit +
        '&autoFallback=' +
        autoFallback,

      getCliffhangerAnalytics: (
        startDate: string,
        endDate: string,
        autoFallback = false
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/cliffhanger-analytics/insights?startDate=` +
        startDate +
        '&endDate=' +
        endDate +
        '&autoFallback=' +
        autoFallback,

      getCliffhangerChartData: (
        startDate: string,
        endDate: string,
        selectedIcon: string
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/cliffhanger-analytics/insights?startDate=` +
        startDate +
        '&endDate=' +
        endDate +
        '&selectedIcon=' +
        selectedIcon,
      getCallRailLogs: (
        startDate: string,
        endDate: string,
        unit: string,
        page: number,
        pageSize: number,
        search: string
      ) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/call-rail/dashboard/call-logs?startDate=` +
        startDate +
        '&endDate=' +
        endDate +
        '&unit=' +
        unit +
        '&page=' +
        page +
        '&pageSize=' +
        pageSize +
        '&search=' +
        search,
    },

    // Monthly Meta / Google / Combined reports. Reading is one stored row per month; only
    // `generate` (support) calls the ad platforms, in the background.
    PAID_MEDIA_REPORTS: {
      availability: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/paid-media-reports/availability`,
      list: (year: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/paid-media-reports?year=${year}`,
      report: (id: number) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/paid-media-reports/${id}`,
      generate: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/paid-media-reports/generate`,
      setup: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/paid-media-reports/setup`,
    },

    // Executive dashboard: built on request from platform data, GA4, Search Console, paid media and
    // the Website Scorecard. Highlights, setup and discovery are support only.
    EXECUTIVE_REPORT: {
      availability: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report/availability`,
      report: (period: string, month?: string | null) =>
        `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report?period=${period}` +
        (month ? `&month=${month}` : ''),
      highlights: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report/highlights`,
      generateHighlights: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report/highlights/generate`,
      setup: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report/setup`,
      discover: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/executive-report/setup/discover`,
    },
  },

  APPOINTMENTS: {
    getAllAppointments: `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/all`,
    deleteAppointment: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/${id}`,
    getAppointmentDetail: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/${id}`,
    getDates: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/provider/${id}/schedules/dates`,
    getTimeSlot: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/provider/${id}/schedules/times`,
    createAppointment: `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments`,
    updateAppointment: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/${id}`,
    massDelete: `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/mass-delete`,
    exportBulkAppointments: `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/export/list`,
    getAppointmentFilterDetails: `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/dashboard?serviceId=0&appointmentStats=true`,
  },

  PAYMENT_ENDPOINTS: {
    getPaymentList: (search: string, page: number, size: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/appointments/page?search=${search}&page=${page}&size=${size}`,
    getPaymentDetails: (id: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/${id}`,
    sendEmail: (id: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/payment/appointment/${id}/email`,
    deletePatientService: (
      paymentId: number | null,
      serviceId: number | null
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/${paymentId}/service/${serviceId}`,
    updatePatientService: (paymentId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/${paymentId}/service`,
    getAvailableService: (clinicId: number, providerId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/services/clinics/${clinicId}?providerId=${providerId}`,
    createPatientService: (serviceIds: number[], paymentId: number | null) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/appointments/${paymentId}/services?serviceIds=${serviceIds.join(',')}`,
  },

  SELF_ASSESSMENT_ENDPOINTS: {
    getSelfAssessment: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptomModelService`,
    v1leadCaptureFormId: `${SERVICE_PREFIX.PRODUCT_V1}/v1/questionnaire/lead-capture-form-id`,
    v1consultationServices: `${SERVICE_PREFIX.PRODUCT_V1}/v1/consultationServices`,
    v1consultationService: `${SERVICE_PREFIX.PRODUCT_V1}/v1/consultationService`,
    updateDescription: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/description/${id}`,
    updateMaleDescription: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/maleDescription/${id}`,
    updateSymptomModel: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptomModelService/${id}`,
    hideSelectedSymptoms: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/hideSelectedSymptoms/${id}`,
    showMaleModelFirst: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/showMaleModelFirst/${id}`,
    genderToHide: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/genderToHide/${id}`,
    addSymptoms: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptoms`,
    updateSymptoms: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptoms/${id}`,
    addConsultationServices: `${SERVICE_PREFIX.PRODUCT_SVC}/consultationServices`,
    v1updateConsultationServices: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/consultationServices/${id}`,
    deleteConsultationServices: (id: number, symptomId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/consultationServices?consultationServiceId=${id}&symptomId=${symptomId}`,
  },

  FILE_MANAGER: {
    getFolderListWithPagination: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/library/folder`,
    deleteFolder: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/fileManager/folder/${folderId}`,
    getSubfoldersAndFiles: (
      folderId: number,
      tags = '',
      page = 0,
      size = 1000,
      search = ''
    ) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/libraries/bytags/${folderId}?tags=${tags}&page=${page}&size=${size}&search=${search}`,
    getFolderById: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/fileManager/folder/${folderId}`,
    getFileById: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/libraries/${fileId}`,
    addFolder: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/fileManager/folder`,
    editFolder: (folderId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/fileManager/folder/${folderId}`,
    addFilesToFolder: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/library`,
    editFile: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/library/${fileId}`,
    deleteFile: (fileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/library/${fileId}`,
    bulkDownload: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/download-zip`,
    downloadFile: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/download/file`,
  },

  TAGS: {
    getTags: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/tag/list`,
    addTag: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/tag`,
    updateTag: (tagId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/tag/${tagId}`,
    deleteTag: (tagId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/tag/${tagId}`,
    getTagById: (tagId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-manager/tag/${tagId}`,
  },

  SOCIAL_MEDIA_POST: SOCIAL_MEDIA_ENDPOINTS.SOCIAL_MEDIA_POST,

  SOCIAL_MEDIA_PROFILES: {
    getProfiles: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/social-profiles?isPostingAllowed=true`,
  },

  SOCIAL_MEDIA_LABELS: {
    getLabels: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPostLabels/list`,
    addLabel: () => `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPostLabel`,
    deleteLabel: (tagId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPostLabel/${tagId}`,
    updateLabel: (tagId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/socialMediaPostLabel/${tagId}`,
  },

  SOCIAL_MEDIA_FILE_MANAGER: SOCIAL_MEDIA_ENDPOINTS.SOCIAL_MEDIA_FILE_MANAGER,
  SOCIAL_MEDIA_TAGS: SOCIAL_MEDIA_ENDPOINTS.SOCIAL_MEDIA_TAGS,

  CONTACT_SETTINGS: {
    updateContactSettings: () => '/api/contactSettings',
  },

  SOCIAL_MEDIA_INTEGRATIONS: {
    instagram: () => `${SERVICE_PREFIX.PRODUCT_SVC}/social-profile/accounts`,
    toggle: () => `${SERVICE_PREFIX.PRODUCT_SVC}/social-profile`,
    resubscribe: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/social-profile/resubscribe/${id}`,
    disconnectProfile: (id: number, isPage?: boolean) =>
      isPage !== undefined
        ? `${SERVICE_PREFIX.PRODUCT_V1}/v1/social-profiles/${id}?isPage=${isPage}`
        : `${SERVICE_PREFIX.PRODUCT_V1}/v1/social-profiles/${id}`,
    messageToggle: (id: number, type: string, socialProfileId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/social-profile?socialProfileType=${type}&id=${id}&socialProfileId=${socialProfileId}`,
    createSocialProfile: () => `${SERVICE_PREFIX.PRODUCT_SVC}/social-profiles`,
  },

  LEAD_STATUS_AUTOMATION: {
    list: (page: number, size: number, search: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules?page=${page}&size=${size}&search=${search}`,

    create: () => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules`,
    update: (id: number) => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules/${id}`,
    delete: (id: number) => `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules/${id}`,
    getById: (id: number) => `${SERVICE_PREFIX.PRODUCT_V1}/v1/rules/${id}`,
    enableAutomation: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules/enableAutomation`,
    toggleStatusForIndividualRule: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/rules/${id}/status`,
  },

  INTEGRATION: {
    metaCapiTestEvent: `${SERVICE_PREFIX.PRODUCT_SVC}/meta-capi/test-event`,
    metaCapiVerify: `${SERVICE_PREFIX.PRODUCT_SVC}/meta-capi/verify`,
    saveApiIntegrationRecords: () =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations`,
    getApiIntegrationRecords: (type: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations?type=${type}`,
    getIntegrationStatus: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/status`,
    lookupAccountName: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/account-name`,
    integrationToggle: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/enable`,
    pullIntegrationToggle: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/pull/enable`,
    getIntegrationHistory: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/histories`,
    getIntegrationHistoryCount: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/histories/count`,
    getIncomingIntegrationHistory: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/incoming/histories`,
    retryThirdPartyIntegration: (id: number, type: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/thirdParty?id=${id}&type=${type}`,
    getPodiumAuthorizeUrl: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/podium/authorize-url`,
    getPodiumReadiness: `${SERVICE_PREFIX.PRODUCT_SVC}/api-integrations/podium/readiness`,
    deleteApiIntegration: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/api-integrations/${id}`,
  },

  IMPORT_FILES: {
    getUploadHistories: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/import`, // Base URL
    downloadFile: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/import/${id}?download=true`,
    importFileWithUserIdAndImportType: (userId: number, importType: string) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/import?userId=${userId}&type=${importType}`,
    getMappingsForFile: (fileId: number, userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/import/${fileId}/mappings?userId=${userId}`,
    processImport: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/import/${id}/process`,
    requestOtp: (userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-upload/request-otp?userId=${userId}`,
    validateOtp: (otp: string, userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/file-upload/validate-otp?otp=${encodeURIComponent(otp)}&userId=${userId}`,
  },

  NOTIFICATION_ENDPOINTS: {
    base: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/notifications-events`,
    markRead: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/notification-events/${id}`,
    markAllRead: (userId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/notification-events/read-all?userId=${userId}`,
  },

  BUSINESS_METRICS_ENDPOINTS: {
    facebookAds: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/facebook-ads-insights?fromDate=${fromDate}`,
    googleAds: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/google-ads?fromDate=${fromDate}`,
    gbpBusiness: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/gbp-business-insights?fromDate=${fromDate}`,
    gbpReviews: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/gbp-reviews?fromDate=${fromDate}`,
    googleAnalytics: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/google-analytics?fromDate=${fromDate}`,
    leads: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/leads?fromDate=${fromDate}`,
    callrailCalls: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/callrail-calls?fromDate=${fromDate}`,
    websiteSpeed: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/performance-desktop-scores?fromDate=${fromDate}`,
    security: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/security-scores?fromDate=${fromDate}`,
    seo: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/seo-scores?fromDate=${fromDate}`,
    downloadExcel: (businessId: string, fromDate: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/businesses/${businessId}/comprehensive-data/download?fromDate=${fromDate}`,
  },

  DENTAL_ASSESSMENT_ENDPOINTS: {
    getAllServices: `${SERVICE_PREFIX.PRODUCT_V1}/v1/services/all`,
    getAllSymptoms: (bodyPart: string, gender: string) =>
      `${SERVICE_PREFIX.PRODUCT_V1}/v1/consultationService?bodyPart=${bodyPart}&gender=${gender}`,

    saveDentalAssessment: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptoms/${id}`,
    createDentalAssessment: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptoms`,
    addConsultationServices: `${SERVICE_PREFIX.PRODUCT_SVC}/consultationServices`,
    updateConsultationServices: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC}/consultationServices/${id}`,
    getDentalAssessment: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/symptomModelService`,
    updateDentalDescription: (id: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/dentalDescription/${id}`,
  },

  AI_BLOGS: {
    blogs: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/blogs`,
    drafts: (businessId: string | number) => `/api/blogs/drafts/${businessId}`,
    publish: '/api/blogs/publish',
    uploadImage: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/ai/blogs/image`,
  },
  // ai-service: businessmaster rows (site -> Meta, GBP, GA4, Google Ads, CallRail IDs). Support/super admin.
  BUSINESS_MASTER: {
    base: '/api/v1/business-master',
    plans: '/api/v1/business-master/plans',
    byId: (id: number) => `/api/v1/business-master/${id}`,
  },
  AI_CRYSTAL_BALL_ENDPOINTS: {
    chat: '/api/v1/crystal-ball/chat',
    chatHistory: '/api/v1/crystal-ball/chat-history',
    clearChatHistory: '/api/v1/crystal-ball/clear-chat-history',
  },

  // Session-scoped page-view tracking, written to our own database. Distinct from the
  // Amplitude proxy under PUBLIC_V2_ENDPOINTS.analyticsTrack, which is unauthenticated and
  // forwards to a third party. These are authenticated: the server takes user and tenant from
  // the JWT rather than believing the request body.
  // Paths carry the product-svc-ext gateway prefix like every other product-service endpoint.
  // Locally, local-api.config rewrites '/product-svc-ext/api' back to '/api' so the same
  // constants reach localhost:8181 unchanged.
  SESSION_ANALYTICS: {
    sessionStart: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions`,
    sessionEvents: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions/events`,
    sessionEnd: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions/end`,
    sessions: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions`,
    sessionByLogin: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions/by-login`,
    sessionsByUser: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions/by-user`,
    sessionTimeline: (sessionId: number) =>
      `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/sessions/${sessionId}/timeline`,
    reportSummary: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/reports/summary`,
    reportPageTime: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/reports/page-time`,
    reportNavigationFlow: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/analytics/reports/navigation-flow`,
  },

  APPLICATION_ANALYTICS: {
    authLogs: '/auth/logs',
    authLogMetrics: '/auth/logs/metrics',
    passwordChangeStats: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/password-change/stats`,
    passwordChangeLogs: `${SERVICE_PREFIX.PRODUCT_SVC_EXT}/password-change/logs`,
  },
  ONBOARDING_WIZARD: {
    getWizard: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}`,
    saveAnswers: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/answers`,
    submit: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/submit`,
    submitPart: (token: string, part: number) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/submit?part=${part}`,
    defer: (token: string, key: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/defer?key=${encodeURIComponent(
        key
      )}`,
    undefer: (token: string, key: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/undefer?key=${encodeURIComponent(
        key
      )}`,
    uploadFile: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/upload`,
    // Bulk locations spreadsheet: template download + upload-and-check.
    locationsTemplate: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/locations/template`,
    importLocations: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/${token}/locations/import`,
    locationsTemplateForEditor: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/locations/template`,
    importLocationsForEditor: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/locations/import`,
    // Editor link (internal, edit-only): same draft, no submit, client progress untouched.
    getWizardForEditor: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}`,
    saveAnswersForEditor: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/answers`,
    uploadFileForEditor: (token: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/upload`,
    // Skipping / un-skipping is an edit to the shared answer, so the editor may do both.
    deferForEditor: (token: string, key: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/defer?key=${encodeURIComponent(
        key
      )}`,
    undeferForEditor: (token: string, key: string) =>
      `${SERVICE_PREFIX.PUBLIC_V2}/onboarding/editor/${token}/undefer?key=${encodeURIComponent(
        key
      )}`,
  },
};
